import { matchQuery, useQueryClient, type InfiniteData, type Query } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'

import { policyCatalogKeys, policyDefinitionKeys, policyOrderKeys, policyResultKeys, type PolicySummary, type PolicyOrderEntry } from '@/modules/hierarchy'
import { printerPolicyApplicabilityKeys, type PrinterPolicyApplicability, type PrinterPolicyValueType } from '../../api/printer-policy-applicability-api'
import { printerPolicyDevicesKeys } from '../../api/printer-policy-devices-api'
import type { PrinterPolicyDefinition } from './printer-policy-definition-query'

type CatalogPage = { items: PolicySummary[]; nextCursor: string | null; hasMore: boolean }

export function usePrinterPolicyReconciliation(entityId: string, valueType: PrinterPolicyValueType) {
  const client = useQueryClient()
  const router = useRouter()

  async function reload(deletedPolicyId?: string) {
    const deletedResultKeys = deletedPolicyId === undefined ? [] : [
      printerPolicyApplicabilityKeys.policy(entityId, deletedPolicyId, valueType),
      printerPolicyDevicesKeys.policy(entityId, valueType, deletedPolicyId),
    ]
    const definitions = { queryKey: policyDefinitionKeys.all(entityId),
      predicate: (query: Query) => query.queryKey[2] !== deletedPolicyId }
    const results = { queryKey: policyResultKeys.type(entityId, valueType),
      predicate: (query: Query) => !deletedResultKeys.some((queryKey) => matchQuery({ queryKey }, query)) }
    const refreshKeys = [policyCatalogKeys.all(entityId), policyOrderKeys.type(entityId, valueType)]
    await Promise.all([
      ...refreshKeys.map((queryKey) => client.cancelQueries({ queryKey })),
      client.cancelQueries(definitions),
      client.cancelQueries(results),
    ])
    await Promise.all([
      ...refreshKeys.map((queryKey) => client.invalidateQueries({ queryKey })),
      client.invalidateQueries(definitions),
      client.invalidateQueries(results),
    ])
  }

  async function deleted(policyId: string) {
    const definition = { queryKey: [...policyDefinitionKeys.all(entityId), policyId] }
    await Promise.all([
      client.cancelQueries(definition),
      client.cancelQueries({ queryKey: policyCatalogKeys.all(entityId) }),
      client.cancelQueries({ queryKey: policyOrderKeys.type(entityId, valueType) }),
      client.cancelQueries({ queryKey: policyResultKeys.type(entityId, valueType) }),
    ])
    // Keep deleted evidence empty even if a catalog refresh fails or an observer stays mounted.
    client.setQueriesData<PrinterPolicyDefinition | null>(definition, null)
    client.setQueriesData<PrinterPolicyApplicability | null>({
      queryKey: printerPolicyApplicabilityKeys.policy(entityId, policyId, valueType),
    }, null)
    client.setQueriesData({ queryKey: printerPolicyDevicesKeys.policy(entityId, valueType, policyId) }, null)
    client.setQueriesData<InfiniteData<CatalogPage>>({ queryKey: policyCatalogKeys.all(entityId) }, (data) => data && ({
      ...data, pages: data.pages.map((page) => ({ ...page, items: page.items.filter((policy) => policy.id !== policyId) })),
    }))
    client.setQueriesData<PolicyOrderEntry[]>({ queryKey: policyOrderKeys.type(entityId, valueType) }, (data) => data?.filter((policy) => policy.id !== policyId))
    await reload(policyId)
    const location = router.state.location
    const pathname = location.pathname.replace(/\/+$/, '').toLowerCase()
    const listPath = `/${entityId}/assets/configuration/printer-policies`.toLowerCase()
    const selectedId = typeof location.search.policy === 'string' ? location.search.policy.toLowerCase() : undefined
    if (pathname === `${listPath}/${policyId.toLowerCase()}`
      || (pathname === listPath && selectedId === policyId.toLowerCase())) {
      await router.navigate({ to: '/$entityId/assets/configuration/printer-policies', params: { entityId },
        search: (current) => typeof current.policy === 'string' && current.policy.toLowerCase() === policyId.toLowerCase()
          ? { ...current, policy: undefined } : current,
        replace: true, resetScroll: false, ignoreBlocker: true })
    }
  }

  return { deleted, reload }
}
